import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CustomSnackbar from "../Utils/CustomSnackbar";
import { updatePassword } from "../api/action";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../redux/slices/userSlice";

export default function ChangePasswordModal({ open, setOpen }) {
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  // const userId = localStorage.getItem("userId");
     const userId = useSelector((state) => state?.user?.userId)

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
       const forcePassword = useSelector((state) => state?.user?.forcePassword)

  useEffect(() => {

    // const forcePassword = localStorage.getItem("forcePassword");
    if (forcePassword === "true") {
      setOpen(true);
      showSnackbar("Please reset your password", "warning");
    }
  }, []);

  const passwordRules = [
    { text: "At least 8 characters long", test: (p) => p.length >= 8 },
    { text: "One uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
    { text: "One lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
    { text: "One number (0–9)", test: (p) => /\d/.test(p) },
    {
      text: "One special character (@$!%*?&)",
      test: (p) => /[@$!%*?&]/.test(p),
    },
  ];

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSubmit = () => {
    if (!form.currentPassword) {
      showSnackbar("Please enter your current password", "warning");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      showSnackbar("New password and confirm password do not match!", "error");
      return;
    }

    const allRulesPassed = passwordRules.every((rule) =>
      rule.test(form.newPassword)
    );

    if (!allRulesPassed) {
      showSnackbar("Password does not meet all security requirements", "error");
      return;
    }

    setLoading(true);

    const payload = {
      user_id: userId,
      current_password: form.currentPassword,
      new_password: form.newPassword,
      confirm_password: form.confirmPassword,
    };
    dispatch(
      updatePassword(
        payload,
        (data) => {
          setLoading(false);
          showSnackbar(data?.data?.message, "success");
          setForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          handleClose();
          // localStorage.setItem("forcePassword", "false");
          dispatch(setUser({ forcePassword: false }));
        },
        (error) => {
          setLoading(false);
          const msg =
            error.message === "Network Error"
              ? error.message
              : error?.response?.data?.error ||
                "Session expired. Please log in again.";
          showSnackbar(msg, "error");
        }
      )
    );
  };

  const handleCloseModal = () => {
    setLoading(true);

    const payload = {
      user_id: userId,
      force_password_status: "false",
    };
    dispatch(
      updatePassword(
        payload,
        (data) => {
          setLoading(false);
          //  localStorage.setItem("forcePassword", "false");
          dispatch(setUser({ forcePassword: false }));
          handleClose();
        },
        (error) => {
          setLoading(false);
          const msg =
            error.message === "Network Error"
              ? error.message
              : error?.response?.data?.error ||
                "Session expired. Please log in again.";
          showSnackbar(msg, "error");
          handleClose();
        }
      )
    );
  };

  return (
    <>
      {/* Modal Dialog */}
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "rgba(29, 57, 105, 1)" }}>
          Change Password
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box>
              <InputLabel shrink>Current Password</InputLabel>
              <TextField
                type="password"
                name="currentPassword"
                value={form.currentPassword}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            <Box>
              <InputLabel shrink>New Password</InputLabel>
              <TextField
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={handleChange}
                fullWidth
              />
            </Box>

            {/* Password Rules */}
            <Box sx={{ mt: 1 }}>
              {passwordRules.map((rule, idx) => {
                const passed = rule.test(form.newPassword);
                return (
                  <Box key={idx} display="flex" alignItems="center" gap={1}>
                    {passed ? (
                      <CheckCircleIcon sx={{ color: "green", fontSize: 18 }} />
                    ) : (
                      <CancelIcon sx={{ color: "gray", fontSize: 18 }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: passed ? "green" : "gray" }}
                    >
                      {rule.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box>
              <InputLabel shrink>Confirm Password</InputLabel>
              <TextField
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={handleSubmit}
            variant="contained"
            sx={{ minWidth: "5rem" }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff !important" }} />
            ) : (
              "Update Password"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
}
